import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/api';
import '../assets/FinanceICAssignmentModal.css';
import '../assets/IcAssignmentModal.css';

/**
 * Combined "Manage IC" modal for Admin → Tutors.
 *
 * One single view (no tabs) where an admin can:
 *   - MANAGE the lists: enable/disable tutors as Finance ICs, and add/delete
 *     Payment ICs (with confirm-before-delete),
 *   - ASSIGN a Finance IC and/or a Payment IC to selected tutees in one action,
 *   - see EVERY tutee with their current Finance IC + Payment IC.
 *
 * Styled with the same classes as FinanceICAssignmentModal (per-invoice).
 */
const IcAssignmentModal = ({ isOpen, onClose }) => {
  const [financeICs, setFinanceICs] = useState([]); // eligible (for dropdown)
  const [tutors, setTutors] = useState([]);         // all tutors (for enable/disable)
  const [paymentICs, setPaymentICs] = useState([]);
  const [paymentICUsage, setPaymentICUsage] = useState({});
  const [financeICUsage, setFinanceICUsage] = useState({});
  const [tutees, setTutees] = useState([]);
  const [stats, setStats] = useState({ totalTutees: 0, missingFinanceIC: 0, missingPaymentIC: 0 });

  const [selectedFinanceIC, setSelectedFinanceIC] = useState('');
  const [selectedPaymentIC, setSelectedPaymentIC] = useState('');
  const [selectedTutees, setSelectedTutees] = useState([]);
  const [updateDraftInvoices, setUpdateDraftInvoices] = useState(true);

  const [search, setSearch] = useState('');
  // Filters for the tutee list. '', a specific IC id, or '__none__' (unassigned)
  const [filterFinanceIC, setFilterFinanceIC] = useState('');
  const [filterPaymentIC, setFilterPaymentIC] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [busy, setBusy] = useState(false); // manage-list actions in flight
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Manage-lists panel
  const [showManage, setShowManage] = useState(false);
  const [newPaymentICName, setNewPaymentICName] = useState('');
  const [newPaymentICNumber, setNewPaymentICNumber] = useState('');
  const [confirmDeleteIC, setConfirmDeleteIC] = useState(null); // PaymentIC pending delete

  const flashError = (msg) => { setError(msg); setTimeout(() => setError(''), 4000); };
  const flashSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3500); };

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.getIcAssignmentsOverview();
      const data = res.data?.data;
      if (data) {
        setFinanceICs(data.financeICs || []);
        setTutors(data.tutors || []);
        setPaymentICs(data.paymentICs || []);
        setPaymentICUsage(data.paymentICUsage || {});
        setFinanceICUsage(data.financeICUsage || {});
        setTutees(data.tutees || []);
        setStats(data.statistics || { totalTutees: 0, missingFinanceIC: 0, missingPaymentIC: 0 });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSelectedFinanceIC('');
      setSelectedPaymentIC('');
      setSelectedTutees([]);
      setSearch('');
      setFilterFinanceIC('');
      setFilterPaymentIC('');
      setError('');
      setSuccess('');
      setShowManage(false);
      setNewPaymentICName('');
      setNewPaymentICNumber('');
      setConfirmDeleteIC(null);
      loadOverview();
    }
  }, [isOpen, loadOverview]);

  if (!isOpen) return null;

  const matchesICFilter = (assigned, filterVal) => {
    if (!filterVal) return true;
    if (filterVal === '__none__') return !assigned;
    return assigned && assigned._id === filterVal;
  };

  const filtered = tutees.filter(t => {
    if (!matchesICFilter(t.financeIC, filterFinanceIC)) return false;
    if (!matchesICFilter(t.paymentIC, filterPaymentIC)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (t.name || '').toLowerCase().includes(q) || (t.code || '').toLowerCase().includes(q);
  });

  const toggleTutee = (id) => {
    setSelectedTutees(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAllFiltered = () => {
    const ids = filtered.map(t => t._id);
    const allSelected = ids.every(id => selectedTutees.includes(id));
    setSelectedTutees(allSelected
      ? selectedTutees.filter(id => !ids.includes(id))
      : Array.from(new Set([...selectedTutees, ...ids])));
  };

  const handleApply = async () => {
    if (selectedTutees.length === 0) { flashError('Select at least one tutee.'); return; }
    if (!selectedFinanceIC && !selectedPaymentIC) {
      flashError('Pick a Finance IC and/or a Payment IC to apply.');
      return;
    }
    setApplying(true);
    setError('');
    setSuccess('');
    try {
      const actions = [];
      if (selectedFinanceIC) {
        actions.push(adminApi.assignDefaultFinanceIC(selectedTutees, selectedFinanceIC, updateDraftInvoices));
      }
      if (selectedPaymentIC) {
        actions.push(adminApi.assignDefaultPaymentIC(selectedTutees, selectedPaymentIC, updateDraftInvoices));
      }
      await Promise.all(actions);

      const parts = [];
      if (selectedFinanceIC) parts.push('Finance IC');
      if (selectedPaymentIC) parts.push('Payment IC');
      flashSuccess(`Updated ${parts.join(' + ')} for ${selectedTutees.length} tutee(s).`);
      setSelectedTutees([]);
      await loadOverview();
    } catch (err) {
      flashError(err.response?.data?.message || 'Failed to apply assignment.');
    } finally {
      setApplying(false);
    }
  };

  const clearFinanceIC = async (tuteeId) => {
    try { await adminApi.removeDefaultFinanceIC([tuteeId]); await loadOverview(); }
    catch (err) { flashError(err.response?.data?.message || 'Failed to clear Finance IC.'); }
  };

  const clearPaymentIC = async (tuteeId) => {
    try { await adminApi.removeDefaultPaymentIC([tuteeId]); await loadOverview(); }
    catch (err) { flashError(err.response?.data?.message || 'Failed to clear Payment IC.'); }
  };

  // ---- Manage lists ----
  const toggleFinanceICEligibility = async (tutor) => {
    setBusy(true);
    try {
      await adminApi.toggleFinanceICEligibility(tutor._id, {
        isEligible: !tutor.isFinanceIC,
        isActive: !tutor.isFinanceIC
      });
      flashSuccess(`${tutor.name} ${tutor.isFinanceIC ? 'removed as' : 'enabled as'} Finance IC.`);
      await loadOverview();
    } catch (err) {
      flashError(err.response?.data?.message || 'Failed to update Finance IC.');
    } finally {
      setBusy(false);
    }
  };

  const addPaymentIC = async () => {
    if (!newPaymentICName.trim() || !newPaymentICNumber.trim()) {
      flashError('Enter both the PayNow name and number.');
      return;
    }
    setBusy(true);
    try {
      await adminApi.createPaymentIC({
        paynowName: newPaymentICName.trim(),
        paynowNumber: newPaymentICNumber.trim()
      });
      flashSuccess(`Added Payment IC ${newPaymentICName.trim()}.`);
      setNewPaymentICName('');
      setNewPaymentICNumber('');
      await loadOverview();
    } catch (err) {
      flashError(err.response?.data?.message || 'Failed to add Payment IC.');
    } finally {
      setBusy(false);
    }
  };

  const doDeletePaymentIC = async () => {
    if (!confirmDeleteIC) return;
    setBusy(true);
    try {
      await adminApi.deletePaymentIC(confirmDeleteIC._id);
      flashSuccess(`Deleted Payment IC ${confirmDeleteIC.paynowName}.`);
      setConfirmDeleteIC(null);
      await loadOverview();
    } catch (err) {
      flashError(err.response?.data?.message || 'Failed to delete Payment IC.');
      setConfirmDeleteIC(null);
    } finally {
      setBusy(false);
    }
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every(t => selectedTutees.includes(t._id));

  return (
    <div className="finance-ic-modal-overlay" onClick={onClose}>
      <div className="finance-ic-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="finance-ic-modal-header">
          <div className="header-content">
            <h2>Manage Finance IC &amp; Payment IC</h2>
            <p className="header-subtitle">
              {stats.totalTutees} tutees — {stats.missingFinanceIC} missing Finance IC, {stats.missingPaymentIC} missing Payment IC
            </p>
          </div>
          <button className="ic-modal-close" onClick={onClose} aria-label="Close modal" type="button">
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {/* Content */}
        <div className="finance-ic-modal-content">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="assign-invoices-tab">

            {/* Manage lists toggle */}
            <div className="ic-manage-header">
              <button
                type="button"
                className="ic-manage-toggle"
                onClick={() => setShowManage(v => !v)}
              >
                {showManage ? '▾' : '▸'} Manage Finance ICs &amp; Payment ICs
              </button>
            </div>

            {showManage && (
              <div className="ic-manage-panel">
                {/* Finance IC management: enable/disable tutors */}
                <div className="ic-manage-col">
                  <h4>Finance ICs</h4>
                  <p className="ic-manage-hint">Enable a tutor so they can be assigned to monitor &amp; send invoices.</p>
                  <div className="ic-manage-list">
                    {tutors.map(t => {
                      const count = financeICUsage[t._id] || 0;
                      return (
                        <div key={t._id} className="ic-manage-row">
                          <span className="ic-manage-name">
                            {t.name}
                            {t.isFinanceIC && (
                              <span className="ic-manage-sub"> · {count} tutee{count !== 1 ? 's' : ''}</span>
                            )}
                          </span>
                          <button
                            type="button"
                            className={`ic-toggle-btn ${t.isFinanceIC ? 'on' : ''}`}
                            disabled={busy}
                            onClick={() => toggleFinanceICEligibility(t)}
                          >
                            {t.isFinanceIC ? 'Enabled' : 'Enable'}
                          </button>
                        </div>
                      );
                    })}
                    {tutors.length === 0 && <p className="no-assignments">No tutors found.</p>}
                  </div>
                </div>

                {/* Payment IC management: add + delete */}
                <div className="ic-manage-col">
                  <h4>Payment ICs</h4>
                  <p className="ic-manage-hint">The PayNow accounts that collect payment.</p>
                  <div className="ic-manage-list">
                    {paymentICs.map(ic => {
                      const used = paymentICUsage[ic._id] || 0;
                      return (
                        <div key={ic._id} className="ic-manage-row">
                          <span className="ic-manage-name">
                            {ic.paynowName} <span className="ic-manage-sub">— {ic.paynowNumber}{used ? ` · ${used} tutee${used !== 1 ? 's' : ''}` : ''}</span>
                          </span>
                          <button
                            type="button"
                            className="ic-delete-btn"
                            disabled={busy}
                            title={used ? `In use by ${used} tutee(s) — reassign first` : 'Delete Payment IC'}
                            onClick={() => setConfirmDeleteIC(ic)}
                          >
                            Delete
                          </button>
                        </div>
                      );
                    })}
                    {paymentICs.length === 0 && <p className="no-assignments">No Payment ICs yet.</p>}
                  </div>

                  {/* Add new Payment IC */}
                  <div className="ic-add-new">
                    <input
                      type="text"
                      className="ic-text-input"
                      placeholder="PayNow name"
                      value={newPaymentICName}
                      onChange={(e) => setNewPaymentICName(e.target.value)}
                    />
                    <input
                      type="text"
                      className="ic-text-input"
                      placeholder="PayNow number"
                      value={newPaymentICNumber}
                      onChange={(e) => setNewPaymentICNumber(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-primary ic-btn-compact"
                      disabled={busy}
                      onClick={addPaymentIC}
                    >
                      Add Payment IC
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Assign pickers */}
            <div className="finance-ic-selector">
              <label>Finance IC (monitors &amp; sends)</label>
              <select value={selectedFinanceIC} onChange={(e) => setSelectedFinanceIC(e.target.value)}>
                <option value="">-- Leave unchanged --</option>
                {financeICs.map(ic => (
                  <option key={ic._id} value={ic._id}>
                    {ic.name} — {financeICUsage[ic._id] || 0} tutee(s)
                  </option>
                ))}
              </select>
            </div>

            <div className="finance-ic-selector">
              <label>Payment IC (collects PayNow)</label>
              <select value={selectedPaymentIC} onChange={(e) => setSelectedPaymentIC(e.target.value)}>
                <option value="">-- Leave unchanged --</option>
                {paymentICs.map(ic => (
                  <option key={ic._id} value={ic._id}>
                    {ic.paynowName} ({ic.paynowNumber}) — {paymentICUsage[ic._id] || 0} tutee(s)
                  </option>
                ))}
              </select>
            </div>

            <div className="update-draft-invoices-option">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={updateDraftInvoices}
                  onChange={(e) => setUpdateDraftInvoices(e.target.checked)}
                />
                <span>Also update existing open (draft) invoices for the selected tutees</span>
              </label>
            </div>

            {/* Search + filters */}
            <div className="ic-filters">
              <input
                type="text"
                className="ic-text-input ic-filter-search"
                placeholder="Search tutee name or code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select
                className="ic-text-input ic-filter-select"
                value={filterFinanceIC}
                onChange={(e) => setFilterFinanceIC(e.target.value)}
              >
                <option value="">All Finance ICs</option>
                <option value="__none__">— No Finance IC —</option>
                {financeICs.map(ic => (
                  <option key={ic._id} value={ic._id}>
                    {ic.name} ({financeICUsage[ic._id] || 0})
                  </option>
                ))}
              </select>
              <select
                className="ic-text-input ic-filter-select"
                value={filterPaymentIC}
                onChange={(e) => setFilterPaymentIC(e.target.value)}
              >
                <option value="">All Payment ICs</option>
                <option value="__none__">— No Payment IC —</option>
                {paymentICs.map(ic => (
                  <option key={ic._id} value={ic._id}>
                    {ic.paynowName} ({paymentICUsage[ic._id] || 0})
                  </option>
                ))}
              </select>
            </div>

            {loading ? (
              <p className="no-assignments">Loading tutees…</p>
            ) : (
              <div className="unassigned-invoices">
                <div className="ic-tutees-toolbar">
                  <h4 className="ic-tutees-title">
                    Tutees ({filtered.length}){selectedTutees.length > 0 ? ` · ${selectedTutees.length} selected` : ''}
                  </h4>
                  <div className="ic-tutees-toolbar-actions">
                    <label className="ic-select-all">
                      <input type="checkbox" checked={allFilteredSelected} onChange={toggleAllFiltered} />
                      <span>Select all shown</span>
                    </label>
                    <button
                      className="btn btn-primary ic-btn-apply"
                      onClick={handleApply}
                      disabled={applying || selectedTutees.length === 0}
                    >
                      {applying ? 'Applying…' : `Apply to ${selectedTutees.length}`}
                    </button>
                  </div>
                </div>

                {/* Column header */}
                <div className="ic-table-head">
                  <span className="ic-col-check" />
                  <span className="ic-col-tutee">Tutee</span>
                  <span className="ic-col-ic">Finance IC</span>
                  <span className="ic-col-ic">Payment IC</span>
                </div>

                <div className="invoices-selection-list ic-table-body">
                  {filtered.map(t => (
                    <label key={t._id} className="ic-table-row">
                      <span className="ic-col-check">
                        <input
                          type="checkbox"
                          checked={selectedTutees.includes(t._id)}
                          onChange={() => toggleTutee(t._id)}
                          disabled={applying}
                        />
                      </span>
                      <span className="ic-col-tutee">
                        <span className="ic-tutee-name">{t.name}</span>
                        <span className="ic-tutee-code">{t.code}</span>
                      </span>
                      <span className="ic-col-ic">
                        {t.financeIC ? (
                          <span className="ic-cell-assigned">
                            <span className="ic-cell-text" title={t.financeIC.name}>{t.financeIC.name}</span>
                            <button
                              type="button"
                              className="btn-remove"
                              title="Clear Finance IC"
                              onClick={(e) => { e.preventDefault(); clearFinanceIC(t._id); }}
                            >×</button>
                          </span>
                        ) : (
                          <span className="ic-cell-empty">—</span>
                        )}
                      </span>
                      <span className="ic-col-ic">
                        {t.paymentIC ? (
                          <span className="ic-cell-assigned">
                            <span className="ic-cell-text" title={`${t.paymentIC.paynowName} · ${t.paymentIC.paynowNumber}`}>{t.paymentIC.paynowName}</span>
                            <button
                              type="button"
                              className="btn-remove"
                              title="Clear Payment IC"
                              onClick={(e) => { e.preventDefault(); clearPaymentIC(t._id); }}
                            >×</button>
                          </span>
                        ) : (
                          <span className="ic-cell-empty">—</span>
                        )}
                      </span>
                    </label>
                  ))}
                  {filtered.length === 0 && <p className="no-assignments">No tutees match your filters.</p>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="finance-ic-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={applying}>Close</button>
        </div>

        {/* Delete confirm dialog */}
        {confirmDeleteIC && (
          <div className="ic-confirm-overlay" onClick={() => setConfirmDeleteIC(null)}>
            <div className="ic-confirm-box" onClick={e => e.stopPropagation()}>
              <h3>Delete Payment IC?</h3>
              <p>
                Delete <strong>{confirmDeleteIC.paynowName}</strong> ({confirmDeleteIC.paynowNumber})?
                {(paymentICUsage[confirmDeleteIC._id] || 0) > 0 && (
                  <> This IC is used by {paymentICUsage[confirmDeleteIC._id]} tutee(s); deletion will be blocked until you reassign them.</>
                )}
              </p>
              <div className="ic-confirm-actions">
                <button className="btn btn-secondary ic-btn-compact" onClick={() => setConfirmDeleteIC(null)} disabled={busy}>Cancel</button>
                <button className="ic-delete-btn ic-btn-compact" onClick={doDeletePaymentIC} disabled={busy}>
                  {busy ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IcAssignmentModal;
